import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaCircle,
  FaComments,
  FaHeart,
  FaMapMarkerAlt
} from 'react-icons/fa';

import UserModal from './UserModal';
import '../css/UserDetails.css';

function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [liking, setLiking] = useState(false);

  const token = localStorage.getItem('authToken');

  const authorizationHeader = useMemo(() => {
    if (!token) return null;
    return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }, [token]);

  useEffect(() => {
    if (!authorizationHeader || !id) {
      setLoading(false);
      setPageError(
        t(
          'user.authenticationRequired',
          'Vous devez être connecté pour voir ce profil.'
        )
      );
      return;
    }

    const controller = new AbortController();

    const loadUser = async () => {
      setLoading(true);
      setPageError('');

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/${id}/details`,
          {
            headers: {
              Authorization: authorizationHeader
            },
            signal: controller.signal
          }
        );

        if (!response.ok) {
          throw new Error(`User details request failed: ${response.status}`);
        }

        const data = await response.json();
        setUser(data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('❌ Error fetching user details:', error);
          setPageError(
            t(
              'user.profileLoadError',
              'Impossible de charger ce profil.'
            )
          );
        }
      } finally {
        setLoading(false);
      }
    };

    loadUser();

    return () => controller.abort();
  }, [id, authorizationHeader, t]);

  const handleLovely = async () => {
    if (!authorizationHeader || !user?.id || liking) return;

    setLiking(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/lovely`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authorizationHeader
          },
          body: JSON.stringify({
            receiverId: user.id
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Lovely request failed: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Lovely failed:', error);
      alert(
        t(
          'user.likeError',
          'Impossible d’aimer ce profil pour le moment.'
        )
      );
    } finally {
      setLiking(false);
    }
  };

  const handleChat = () => {
    if (!user) return;

    localStorage.setItem(
      'selectedChatUser',
      JSON.stringify(user)
    );

    navigate('/chat');
  };

  if (loading) {
    return (
      <main className="user-details-page">
        <div className="user-details-state">
          <div className="user-details-spinner" />
          <p>{t('common.loading', 'Chargement...')}</p>
        </div>
      </main>
    );
  }

  if (pageError || !user) {
    return (
      <main className="user-details-page">
        <div className="user-details-state user-details-state--error">
          <p>{pageError || t('user.notFound', 'Profil introuvable.')}</p>

          <button
            type="button"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft />
            {t('common.back', 'Retour')}
          </button>
        </div>
      </main>
    );
  }

  const coverPhoto =
    user.mediaUrls?.[0] ||
    '/default-profile.jpg';

  return (
    <main className="user-details-page">
      <section className="user-details-card">
        <button
          type="button"
          className="user-details-back"
          onClick={() => navigate(-1)}
          aria-label={t('common.back', 'Retour')}
        >
          <FaArrowLeft />
        </button>

        <div
          className="user-details-cover"
          onClick={() => setShowModal(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              setShowModal(true);
            }
          }}
        >
          <img
            src={coverPhoto}
            alt={user.name || 'Profile'}
            onError={(event) => {
              event.currentTarget.src = '/default-profile.jpg';
            }}
          />

          <div className="user-details-cover-shade" />

          <span className="user-details-status">
            <FaCircle
              className={
                user.online
                  ? 'is-online'
                  : 'is-offline'
              }
            />

            {user.online
              ? t('user.online', 'En ligne')
              : t('user.offline', 'Hors ligne')}
          </span>

          <div className="user-details-cover-copy">
            <h1>
              {user.name}
              <span>{user.age}</span>
            </h1>

            <p>
              <FaMapMarkerAlt />
              {user.location ||
                t(
                  'user.locationUnknown',
                  'Localisation non renseignée'
                )}
            </p>
          </div>
        </div>

        <div className="user-details-content">
          <div className="user-details-grid">
            <DetailItem
              label={t('user.gender', 'Genre')}
              value={
                user.gender ||
                t('user.notSpecified', 'Non précisé')
              }
            />

            <DetailItem
              label={t(
                'user.maritalStatus',
                'Situation matrimoniale'
              )}
              value={
                user.maritalStatus ||
                t('user.notSpecified', 'Non précisé')
              }
            />

            <DetailItem
              label={t('user.hasChildren', 'Enfants')}
              value={
                user.hasChildren
                  ? t('user.yes', 'Oui')
                  : t('user.no', 'Non')
              }
            />

            <DetailItem
              label={t('user.skinTone', 'Teint')}
              value={
                user.skinTone ||
                t('user.notSpecified', 'Non précisé')
              }
            />

            {user.city && (
              <DetailItem
                label={t('user.city', 'Ville')}
                value={user.city}
              />
            )}

            {user.state && (
              <DetailItem
                label={t('user.state', 'État')}
                value={user.state}
              />
            )}

            {user.country && (
              <DetailItem
                label={t('user.country', 'Pays')}
                value={user.country}
              />
            )}
          </div>

          {user.bio && (
            <section className="user-details-bio">
              <span>
                {t('user.aboutMe', 'À propos de moi')}
              </span>

              <p>{user.bio}</p>
            </section>
          )}

          <div className="user-details-actions">
            <button
              type="button"
              className="user-details-action user-details-action--like"
              onClick={handleLovely}
              disabled={liking}
            >
              <FaHeart />

              {liking
                ? t('user.liking', 'Envoi...')
                : t('user.like', 'J’aime')}
            </button>

            <button
              type="button"
              className="user-details-action user-details-action--message"
              onClick={handleChat}
            >
              <FaComments />
              {t('user.message', 'Message')}
            </button>
          </div>
        </div>
      </section>

      <UserModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        user={user}
        onLovely={handleLovely}
        onChat={handleChat}
      />
    </main>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="user-details-item">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default UserDetails;